export interface ElementInfo<S extends string, T> {
	Id: S;
	Element: HTMLInputElement;
	DefaultValue: T;
	Getter(): T;
	Setter(v: T): void;
}

export interface StringEncoder<T> {
	ToString(v: T): string;
	FromString(v: string): T;
}

export function CreateElementInfo<S extends string, T>(
	id: S,
	defaultValue: T,
	strEncoder: StringEncoder<T>
): ElementInfo<S, T> {
	const elem = document.getElementById(id)! as HTMLInputElement;

	const getter = () => strEncoder.FromString(elem.value);
	const setter = (v: T) => {
		elem.value = strEncoder.ToString(v);
		if (elem.onchange) {
			elem.onchange(new Event("onchange"))
		}
	};

	return {
		Id: id,
		Element: elem,
		DefaultValue: defaultValue,
		Setter: setter,
		Getter: getter,
	};
}

type ControlGroup<T extends readonly ElementInfo<string, any>[]> = {
    Controls: T;
} & {
    [K in T[number] as K["Id"]]: K;
};

export function CreateControlsGroup<T extends readonly ElementInfo<string, any>[]>(
    ...elements: T
): ControlGroup<T> {
    const result = { Controls: elements } as any;
    
    for (const element of elements) {
        result[element.Id] = element;
    }
    
    return result as ControlGroup<T>;
}

type ControlGroupValues<T extends readonly ElementInfo<string, any>[]> = {
    [K in T[number] as K["Id"]]: ReturnType<K["Getter"]>;
};

export function GetControlGroupValues<T extends readonly ElementInfo<string, any>[]>(
    elements: T
): ControlGroupValues<T> {
    const result = {} as any;
    
    for (const element of elements) {
        result[element.Id] = element.Getter();
    }
    
    return result as ControlGroupValues<T>;
}
